<template>
  <div ref="chatchamp" :class="classes">
    <div
      :class="containerClasses"
      :style="cssVariableStyles"
    >
      <loading-indicator v-if="isLoading" />
      <template v-else>
        <transition
          name="trbo-a-slide-to-up"
          :css="!isEmbedded"
          @after-enter="enableParentsTransformMobile"
        >
          <resize-observer
            v-if="isLandingPage"
            class="trbo-a-chatchamp-wizard-container__landing-page"
            @update-height="setLandingPageHeight"
          >
            <landing-page />
          </resize-observer>
        </transition>
        <transition
          name="trbo-a-slide-from-down"
          :css="!isEmbedded"
          @before-leave="setWizardPageHeight"
          @before-enter="disableParentsTransformMobile"
          @after-enter="setAutoHeight"
        >
          <resize-observer
            v-if="!isLandingPage"
            ref="wizardPagesContainer"
            class="trbo-a-chatchamp-wizard-container__questions-page"
            @update-height="setWizardPagesHeight"
            @scroll="onScroll"
          >
            <wizard-pages @navigate="onNavigate" />
          </resize-observer>
        </transition>
      </template>
    </div>
  </div>
</template>

<script>
// @flow

import { mapGetters, mapActions, mapState } from 'vuex'

import { isMobile } from '../../../common/device'
import { isTopPositive } from '../../../common/viewport'
import { cssWrapper, insertStyle, getParents } from '../../../common/utils'
import { scrollToElementsTop, scrollWindowToElementsTop } from '../../../common/scrolling'
import LandingPage from '../LandingPage/LandingPage.vue'
import WizardPages from '../Questions/WizardPages.vue'
import ResizeObserver from '../ResizeObserver/ResizeObserver.vue'
import { LoadingIndicator } from '../../../common/components'

export default {
  components: {
    LandingPage,
    WizardPages,
    LoadingIndicator,
    ResizeObserver
  },
  data: () => {
    return {
      isLoading: true,
      landingPageHeight: '0px',
      wizardPagesHeight: '0px',
      height: '0px',
      latestScrollTop: 0
    }
  },
  computed: {
    ...mapGetters(['primaryColor', 'isLandingPage', 'locale', 'customCss']),
    ...mapState(['wizard', 'customerId', 'isEmbedded']),
    classes (): Object {
      return {
        'trbo-a-chatchamp': true,
        'trbo-a-chatchamp-wizard': true,
        'trbo-a-chatchamp-wizard--is-embedded': this.isEmbedded
      }
    },
    containerClasses (): Object {
      return {
        'trbo-a-chatchamp-wizard-container': true,
        'trbo-a-chatchamp-wizard-container--is-not-landing-page': !this.isLandingPage,
        'trbo-a-chatchamp-wizard-container--is-embedded': this.isEmbedded
      }
    },
    cssVariableStyles (): Object {
      return { '--primary-color': this.primaryColor, height: this.height }
    }
  },
  watch: {
    locale (val: string): void {
      this.$root.$i18n.locale = val
    },
    landingPageHeight (val: string): void {
      if (this.isLandingPage && !this.isEmbedded) {
        this.height = val
      }
    },
    wizardPagesHeight (val: string): void {
      if (!this.isLandingPage && this.height !== 'auto' && !this.isEmbedded) {
        this.height = val
      }
    },
    isLandingPage (val: boolean): void {
      this.$nextTick(() => {
        if (this.wizard.skipLandingPage) return
        if (this.isEmbedded) return
        if (val) this.height = this.landingPageHeight
        else this.height = this.wizardPagesHeight
      })
    }
  },
  async created (): Promise<void> {
    await this.getCustomerConfig()
    await this.loadWizard()
    if (this.customCss) insertStyle(cssWrapper('.trbo-a-chatchamp', this.customCss))
    this.isLoading = false
    if (this.wizard.skipLandingPage || this.isEmbedded) this.setAutoHeight()
  },
  methods: {
    ...mapActions(['getCustomerConfig', 'loadWizard', 'setScrollDirection']),
    setLandingPageHeight (value: number): void {
      this.landingPageHeight = `${value}px`
    },
    setWizardPagesHeight (value: number): void {
      this.wizardPagesHeight = `${value}px`
    },
    setWizardPageHeight (): void {
      if (this.isEmbedded) return
      this.height = this.wizardPagesHeight
    },
    setAutoHeight (): void {
      this.height = 'auto'
    },
    onScroll (e): void {
      const { scrollTop } = e.target
      this.setScrollDirection(scrollTop > this.latestScrollTop ? 'down' : 'up')
      this.latestScrollTop = scrollTop
    },
    onNavigate (): void {
      if (isMobile()) {
        scrollToElementsTop(this.$refs.wizardPagesContainer.$el, 0)
      } else {
        if (isTopPositive(this.$refs.wizardPagesContainer.$el)) return
        scrollWindowToElementsTop(this.$refs.wizardPagesContainer.$el)
      }
    },
    enableParentsTransformMobile (): void {
      // Thiago; 04.11.2022; it is a workaround due to a bug in the WebKit browsers
      // because when one of the antecessors elements have the transform property
      // it interferes in the fixed position of the element
      getParents(this.$refs.chatchamp).forEach(element => {
        element.classList.remove('trbo-a-chatchamp-disable-transform-mobile')
      })
    },
    disableParentsTransformMobile (): void {
      getParents(this.$refs.chatchamp).forEach(element => {
        element.classList.add('trbo-a-chatchamp-disable-transform-mobile')
      })
    }
  }
}
</script>

<style src="../../../common/styles/preflight.scss" />
<style scoped src="./main.scss" />
